import React, { useState, useEffect, useRef } from 'react';
import './TableSearchBar.scss';
import { Search } from 'react-bootstrap-icons';

interface TableSearchBarProps {
  data: any[];
  updateFilteredData: (filteredData: any[]) => void;
  searchFields: string[];
  placeholder: string;
}

const removeAccents = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Elimina tildes y otros diacríticos
};

const TableSearchBar: React.FC<TableSearchBarProps> = ({ data, updateFilteredData, searchFields, placeholder }) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastInput, setLastInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreNextEffect = useRef(false); // Bandera para ignorar el siguiente useEffect

  useEffect(() => {
    if (ignoreNextEffect.current) {
      ignoreNextEffect.current = false;
      return;
    }
    if (input.trim() === '') {
      updateFilteredData(data);
      setShowSuggestions(false);
    } else {
      const inputNumber = !isNaN(Number(input)) ? Number(input) : null;
      const normalizedInput = removeAccents(input.toLowerCase());
      const filteredData = data.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          if (typeof value === 'string') {
            return removeAccents((value || '').toLowerCase()).includes(normalizedInput);
          } else if (typeof value === 'number' && inputNumber !== null) {
            return value.toString().includes(inputNumber.toString());
          }
          return false;
        })
      );
      updateFilteredData(filteredData);
      const suggestionSet = new Set(
        filteredData.flatMap(item =>
          searchFields
            .map(field => item[field])
            .filter(value =>
              typeof value === 'string'
                ? removeAccents((value || '').toLowerCase()).includes(normalizedInput)
                : typeof value === 'number' && inputNumber !== null
                ? value.toString().includes(inputNumber.toString())
                : false
            )
        )
      );
      setSuggestions(Array.from(suggestionSet).slice(0, 50));
      // Solo mostrar suggestions si el input ha cambiado desde el último click
      if (input !== lastInput) {
        setShowSuggestions(true);
      }
    }
  }, [input, data, updateFilteredData, lastInput]);

  const handleSuggestionClick = (suggestion: any) => {
    const newValue = typeof suggestion === 'string' ? suggestion : suggestion.toString();
    ignoreNextEffect.current = true; // Evita que el useEffect vuelva a mostrar suggestions
    setInput(newValue);
    setShowSuggestions(false);
    setLastInput(newValue); // Guardar el valor seleccionado para controlar la visibilidad
    // Filtra los datos al hacer clic en una sugerencia
    const normalizedValue = removeAccents(newValue.toLowerCase());
    const filteredData = data.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        if (typeof value === 'string') {
          return removeAccents((value || '').toLowerCase()).includes(normalizedValue);
        } else if (typeof value === 'number') {
          return value.toString().includes(newValue);
        }
        return false;
      })
    );
    updateFilteredData(filteredData);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const normalizedInput = removeAccents(input.toLowerCase());
      const filteredData = data.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          if (typeof value === 'string') {
            return removeAccents((value || '').toLowerCase()).includes(normalizedInput);
          } else if (typeof value === 'number') {
            return value.toString().includes(input);
          }
          return false;
        })
      );
      updateFilteredData(filteredData);
      setShowSuggestions(false);
    }
  };

  return (
    <div className="search-bar-container">
      <div className="search-input-container">
        <Search className="search-icon" size={15} />
        <input
          ref={inputRef}
          className="search-input"
          type="text"
          placeholder={placeholder}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setLastInput(''); // Permitir mostrar suggestions nuevamente al modificar el input
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => input && showSuggestions === false && suggestions.length > 0 && setShowSuggestions(true)}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TableSearchBar;
